import { logger } from '@/utils/logger';
import posthog from 'posthog-js';
import React from 'react';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /**
   * If true, will show detailed error information even in production
   * Useful for testing environments
   */
  showDebugInfo?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call the optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Capture error in PostHog
    if (process.env.NODE_ENV === 'production') {
      posthog.captureException(error, {
        componentStack: errorInfo.componentStack,
        errorInfo: errorInfo,
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
      });
    }

    // Log the error in development or when debug info is requested
    if (process.env.NODE_ENV === 'development' || this.props.showDebugInfo) {
      console.group('ErrorBoundary caught an error:');
      logger.error('Error:', 'error', error);
      logger.error('Component Stack:', 'error', errorInfo.componentStack);
      logger.error('Error Stack:', 'error', error.stack || 'No stack trace available');
      if ('cause' in error && error.cause) {
        logger.error('Error Cause:', 'error', error.cause);
      }
      console.groupEnd();
    }
  }

  private isTestEnvironment() {
    return process.env.NODE_ENV === 'test' || process.env.VITEST;
  }

  private renderTestError() {
    const { error, errorInfo } = this.state;
    if (!error) return null;

    return (
      <div className="test-error-details space-y-2">
        <div className="font-mono text-sm">
          <div className="font-bold">Error Message:</div>
          <pre className="whitespace-pre-wrap bg-red-50 p-2 rounded">{error.message}</pre>
        </div>
        {errorInfo && (
          <div className="font-mono text-sm">
            <div className="font-bold">Component Stack:</div>
            <pre className="whitespace-pre-wrap bg-red-50 p-2 rounded">
              {errorInfo.componentStack}
            </pre>
          </div>
        )}
      </div>
    );
  }

  private renderDevError() {
    const { error, errorInfo } = this.state;
    if (!error) return null;

    return (
      <details className="mt-4" open>
        <summary className="cursor-pointer text-red-600 hover:text-red-800 font-semibold">
          Error Details
        </summary>
        <div className="mt-2 p-4 bg-red-100 rounded overflow-auto">
          <div className="mb-4">
            <div className="font-bold mb-2">Error:</div>
            <pre className="whitespace-pre-wrap text-sm bg-white p-2 rounded">
              {error.toString()}
            </pre>
            {error.stack && (
              <>
                <div className="font-bold mt-4 mb-2">Stack Trace:</div>
                <pre className="whitespace-pre-wrap text-sm bg-white p-2 rounded">
                  {error.stack}
                </pre>
              </>
            )}
            {'cause' in error && error.cause !== undefined && (
              <>
                <div className="font-bold mt-4 mb-2">Caused By:</div>
                <pre className="whitespace-pre-wrap text-sm bg-white p-2 rounded">
                  {(() => {
                    try {
                      return JSON.stringify(error.cause, null, 2);
                    } catch {
                      return String(error.cause);
                    }
                  })()}
                </pre>
              </>
            )}
          </div>
          {errorInfo && (
            <div>
              <div className="font-bold mb-2">Component Stack:</div>
              <pre className="whitespace-pre-wrap text-sm bg-white p-2 rounded">
                {errorInfo.componentStack}
              </pre>
            </div>
          )}
        </div>
      </details>
    );
  }

  public render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // If a custom fallback is provided, use it
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const isDev = process.env.NODE_ENV === 'development';
    const isTest = this.isTestEnvironment();

    return (
      <div className="error-boundary p-4 m-4 border border-red-500 rounded-lg bg-red-50">
        <h2 className="text-xl font-bold text-red-700 mb-2">
          {isDev || isTest ? 'Error during rendering' : 'Something went wrong'}
        </h2>

        {isTest && this.renderTestError()}
        {isDev && this.renderDevError()}

        {!isTest && !isDev && (
          <p className="text-red-600 mt-2">
            Please try refreshing the page or contact support if the issue persists.
          </p>
        )}

        {(isDev || isTest) && (
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    );
  }
}
